import React, { useState, useEffect, useRef } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Textarea } from '../ui/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { 
  Tag, 
  Save,
  Plus,
  X,
  GripVertical,
  FileText
} from 'lucide-react';
import { getDepartments } from '../../api/departmentService';
import { getTemplateByCategory, createOrUpdateTemplate } from '../../api/templateService';

function parseFieldOptionsToArray(fieldOptions) {
  if (fieldOptions == null || fieldOptions === '') return [];
  if (Array.isArray(fieldOptions)) {
    return fieldOptions.map((o) => String(o));
  }
  if (typeof fieldOptions !== 'string' || !fieldOptions.trim()) return [];
  try {
    const parsed = JSON.parse(fieldOptions);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((o) => String(o));
  } catch {
    return [];
  }
}

function serializeOptionsArray(arr) {
  const cleaned = arr.map((s) => String(s).trim()).filter(Boolean);
  return JSON.stringify(cleaned);
}

/** First invalid field in visual form order (category name, then each template row). */
function pickFirstValidationErrorKey(errorMap) {
  const keys = Object.keys(errorMap || {});
  if (keys.length === 0) return null;

  const priority = (key) => {
    if (key === 'categoryName') return [0, 0, 0];
    const match = key.match(/^(fieldName|fieldType|fieldOptions)_(\d+)$/);
    if (match) {
      const fieldKind = { fieldName: 0, fieldType: 1, fieldOptions: 2 }[match[1]] ?? 9;
      return [1, Number(match[2]), fieldKind];
    }
    return [2, 0, 0];
  };

  return keys.sort((a, b) => {
    const pa = priority(a);
    const pb = priority(b);
    for (let i = 0; i < 3; i += 1) {
      if (pa[i] !== pb[i]) return pa[i] - pb[i];
    }
    return 0;
  })[0];
}

function focusValidationAnchor(anchor) {
  if (!anchor) return;
  const focusable = anchor.querySelector(
    'input:not([type="checkbox"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [role="combobox"]:not([disabled])'
  );
  if (focusable && typeof focusable.focus === 'function') {
    focusable.focus({ preventScroll: true });
  }
}

/** Dropdown / checkbox options: add row + chips (no JSON typing for end users) */
function FieldOptionsChipsEditor({ value, onChange, error, validationFieldKey }) {
  const [draft, setDraft] = useState('');
  const options = parseFieldOptionsToArray(value);

  useEffect(() => {
    setDraft('');
  }, [value]);

  const addOption = () => {
    const t = draft.trim();
    if (!t) return;
    if (options.includes(t)) {
      setDraft('');
      return;
    }
    onChange(serializeOptionsArray([...options, t]));
    setDraft('');
  };

  return (
    <div className="space-y-2" data-validation-field={validationFieldKey}>
      <Label className="text-sm font-medium text-gray-700">
        Options <span className="text-red-500">*</span>
      </Label>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addOption();
            }
          }}
          placeholder="Type an option, then Add"
          className={`flex-1 ${error ? 'border-red-500' : ''}`}
        />
        <Button type="button" variant="outline" className="shrink-0" onClick={addOption}>
          Add Option
        </Button>
      </div>
      {options.length > 0 ? (
        <div className="flex flex-wrap gap-2 rounded-md border border-gray-200 bg-white p-2 dark:border-gray-600 dark:bg-gray-800/80">
          {options.map((opt, i) => (
            <span
              key={`${opt}-${i}`}
              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            >
              <span className="max-w-[220px] truncate" title={opt}>{opt}</span>
              <button
                type="button"
                onClick={() => onChange(serializeOptionsArray(options.filter((_, j) => j !== i)))}
                className="rounded-full p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
                aria-label={`Remove ${opt}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-500">Add at least one option for this field type.</p>
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

const AddEditCategoryModal = ({ isOpen, onClose, category, onSave, saving = false }) => {
  const [formData, setFormData] = useState({
    categoryName: '',
    description: '',
    departmentId: '',
    isActive: true,
    templateType: '1' // Default to "1" for default fields
  });
  const [errors, setErrors] = useState({});
  const [departments, setDepartments] = useState([]);
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [templateFields, setTemplateFields] = useState([]);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const templateFieldsRef = useRef([]);
  templateFieldsRef.current = templateFields;
  const templateLoadSeqRef = useRef(0);
  const pendingScrollErrorKeyRef = useRef(null);

  // Field types available
  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Select (Dropdown)' },
    { value: 'checkbox', label: 'Checkbox' }
  ];

  // Initialize form + template list when modal opens (avoid clearing template fields on tab switch)
  useEffect(() => {
    if (!isOpen) return;

    if (category) {
      const tpl = String(category.templateType || category.template_type || '1');
      const categoryId = category.categoryId || category.id;
      setFormData({
        categoryName: category.categoryName || category.name || '',
        description: category.description || '',
        departmentId: category.departmentId ? category.departmentId.toString() : (category.department_id ? category.department_id.toString() : ''),
        isActive: category.isActive !== undefined ? category.isActive : (category.status === 'active'),
        templateType: category.templateType || category.template_type || '1'
      });
      setTemplateFields([]);
      if (categoryId && tpl === '2') {
        loadTemplateFields(categoryId);
      }
    } else {
      setFormData({
        categoryName: '',
        description: '',
        departmentId: '',
        isActive: true,
        templateType: '1'
      });
      setTemplateFields([]);
    }
    setErrors({});
  }, [isOpen, category]);

  // Load departments when modal opens
  useEffect(() => {
    if (isOpen) {
      loadDepartments();
    }
  }, [isOpen, category]);

  useEffect(() => {
    if (!isOpen) {
      templateLoadSeqRef.current += 1;
      setLoadingTemplate(false);
      pendingScrollErrorKeyRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    const errorKey = pendingScrollErrorKeyRef.current;
    if (!errorKey || !isOpen || !errors[errorKey]) {
      return;
    }

    pendingScrollErrorKeyRef.current = null;

    const frameId = requestAnimationFrame(() => {
      const anchor = document.querySelector(
        `#category-form [data-validation-field="${CSS.escape(errorKey)}"]`
      );
      if (!anchor) return;

      anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      focusValidationAnchor(anchor);
    });

    return () => cancelAnimationFrame(frameId);
  }, [errors, isOpen]);

  const loadDepartments = async () => {
    setLoadingDepts(true);
    try {
      const response = await getDepartments({ page: 0, size: 1000, isActive: true });
      const deptsData = response?.data?.data?.content || response?.data?.data || response?.data || [];
      setDepartments(Array.isArray(deptsData) ? deptsData : []);
    } catch (error) {
      console.error('Error loading departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepts(false);
    }
  };

  const loadTemplateFields = async (categoryId) => {
    if (!categoryId) {
      setTemplateFields([]);
      return;
    }

    const seq = ++templateLoadSeqRef.current;
    setLoadingTemplate(true);
    try {
      const response = await getTemplateByCategory(categoryId, { includeInactive: true });
      if (seq !== templateLoadSeqRef.current) return;
      const templateData = response?.data?.data;
      if (templateData?.fields && Array.isArray(templateData.fields) && templateData.fields.length > 0) {
        setTemplateFields(templateData.fields.map(field => ({
          fieldId: field.fieldId,
          fieldName: field.fieldName || '',
          fieldCode: field.fieldCode || '',
          fieldType: field.fieldType || 'text',
          fieldOptions:
            field.fieldType === 'select' || field.fieldType === 'checkbox'
              ? (typeof field.fieldOptions === 'string'
                  ? field.fieldOptions
                  : field.fieldOptions != null
                    ? JSON.stringify(field.fieldOptions)
                    : '[]')
              : '',
          isRequired: field.isRequired || false,
          isActive: field.isActive !== false,
          displayOrder: field.displayOrder || 0
        })));
      } else {
        setTemplateFields([]);
      }
    } catch (error) {
      if (seq !== templateLoadSeqRef.current) return;
      console.error('Error loading template fields:', error);
      if (error.response?.status !== 404) {
        console.warn('Unexpected error loading template fields:', error);
      }
      setTemplateFields([]);
    } finally {
      if (seq === templateLoadSeqRef.current) {
        setLoadingTemplate(false);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'templateType' && value === '2') {
      const categoryId = category?.categoryId || category?.id;
      const tpl = String(category?.templateType || category?.template_type || '1');
      if (categoryId && tpl !== '2' && templateFieldsRef.current.length === 0) {
        loadTemplateFields(categoryId);
      }
    }

    if (field === 'templateType' && value === '1') {
      templateLoadSeqRef.current += 1;
      setLoadingTemplate(false);
      if (templateFieldsRef.current.length > 0) {
        setTemplateFields(
          templateFieldsRef.current.map((templateField) => ({
            ...templateField,
            isActive: false,
          }))
        );
      }
      setErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith('fieldName_') || key.startsWith('fieldCode_') ||
              key.startsWith('fieldType_') || key.startsWith('fieldOptions_')) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    }

    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (fieldsToValidate = templateFields) => {
    const newErrors = {};

    if (!category && !formData.categoryName?.trim()) {
      newErrors.categoryName = 'Category name is required';
    }

    if (formData.templateType === '2') {
      fieldsToValidate.forEach((field, index) => {
        if (!field.fieldName?.trim()) {
          newErrors[`fieldName_${index}`] = 'Field name is required';
        }
        if (!field.fieldType) {
          newErrors[`fieldType_${index}`] = 'Field type is required';
        }
        if (field.fieldType === 'select' || field.fieldType === 'checkbox') {
          const opts = parseFieldOptionsToArray(field.fieldOptions);
          if (opts.length === 0) {
            newErrors[`fieldOptions_${index}`] = 'Add at least one option';
          }
        }
      });
    }

    setErrors(newErrors);
    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors,
    };
  };

  const addTemplateField = () => {
    const newField = {
      fieldName: '',
      fieldCode: '',
      fieldType: 'text',
      fieldOptions: '',
      isRequired: false,
      isActive: true,
      displayOrder: templateFields.length
    };
    setTemplateFields([...templateFields, newField]);
  };

  // Helper function to generate field code from field name
  const generateFieldCode = (fieldName) => {
    if (!fieldName || !fieldName.trim()) {
      return '';
    }
    return fieldName
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  };

  const updateTemplateField = (index, field, value) => {
    const updatedFields = [...templateFields];
    updatedFields[index] = { ...updatedFields[index], [field]: value };

    if (field === 'fieldName') {
      const generatedCode = generateFieldCode(value);
      updatedFields[index].fieldCode = generatedCode;
    }

    if (field === 'fieldType') {
      if (value !== 'select' && value !== 'checkbox') {
        updatedFields[index].fieldOptions = '';
      } else if (!updatedFields[index].fieldOptions?.trim()) {
        updatedFields[index].fieldOptions = '[]';
      }
    }

    setTemplateFields(updatedFields);
    
    // Clear error for this field
    const errorKey = `${field}_${index}`;
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const fieldsWithCodes = templateFields.map(field => {
      if (!field.fieldCode?.trim() && field.fieldName?.trim()) {
        return { ...field, fieldCode: generateFieldCode(field.fieldName) };
      }
      return field;
    });
    setTemplateFields(fieldsWithCodes);

    const validation = validateForm(fieldsWithCodes);
    if (!validation.isValid) {
      pendingScrollErrorKeyRef.current = pickFirstValidationErrorKey(validation.errors);
      return;
    }

    const finalTemplateFields = fieldsWithCodes.map((field) => ({
      ...field,
      fieldCode: field.fieldCode || generateFieldCode(field.fieldName),
      isActive: formData.templateType === '2' ? field.isActive !== false : false,
    }));

    const categoryData = {
      // Include categoryName - it's read-only when editing so value cannot be changed
      categoryName: formData.categoryName.trim(),
      description: formData.description?.trim() || null,
      departmentId: formData.departmentId ? parseInt(formData.departmentId) : null,
      isActive: formData.isActive !== undefined ? formData.isActive : true,
      templateType: formData.templateType || '1', // Include template type (1 = default, 2 = custom)
      templateFields: finalTemplateFields // Includes deactivated fields when switching to default
    };

    // Remove null/empty values for optional fields
    if (!categoryData.description) delete categoryData.description;
    if (!categoryData.departmentId) delete categoryData.departmentId;

    onSave(categoryData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={category ? 'Edit Category' : 'Add New Category'}
      size="xl"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="px-6"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="category-form"
            className="flex items-center gap-2 px-6 bg-blue-600 hover:bg-blue-700 text-white"
            disabled={saving}
          >
            <Save className="h-4 w-4" />
            {saving ? (category ? 'Updating...' : 'Creating...') : (category ? 'Update Category' : 'Create Category')}
          </Button>
        </div>
      }
    >
      {/*
        Single scroll surface: Modal body only (see Modal.js). No nested overflow-y / max-height here.
      */}
      <form onSubmit={handleSubmit} id="category-form" className="min-w-0 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
              <Tag className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2" data-validation-field="categoryName">
                <Label htmlFor="categoryName" className="text-sm font-medium text-gray-700">
                  Category Name <span className="text-red-500">*</span>
                </Label>
                {category ? (
                  // Read-only display when editing
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-900 flex items-center min-h-[42px]">
                    <span>{formData.categoryName || 'N/A'}</span>
                  </div>
                ) : (
                  // Editable input when creating new category
                  <Input
                    id="categoryName"
                    value={formData.categoryName}
                    onChange={(e) => handleInputChange('categoryName', e.target.value)}
                    placeholder="Enter category name"
                    className={errors.categoryName ? 'border-red-500' : ''}
                    maxLength={255}
                  />
                )}
                {errors.categoryName && (
                  <p className="text-sm text-red-500">{errors.categoryName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="departmentId" className="text-sm font-medium text-gray-700">
                  Department
                </Label>
                <Select
                  value={formData.departmentId?.toString() || ''}
                  onValueChange={(value) => handleInputChange('departmentId', value || '')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={loadingDepts ? "Loading departments..." : "Select department (optional)"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No department assigned</SelectItem>
                    {loadingDepts ? (
                      <SelectItem value="" disabled>Loading...</SelectItem>
                    ) : departments.length === 0 ? (
                      <SelectItem value="" disabled>No departments available</SelectItem>
                    ) : (
                      departments.map((dept) => (
                        <SelectItem key={dept.departmentId || dept.id} value={(dept.departmentId || dept.id).toString()}>
                          {dept.departmentName || dept.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">Assign this category to a department (optional)</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium text-gray-700">
                Description
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter category description (optional)"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                Status
              </Label>
              <select
                id="isActive"
                value={formData.isActive ? 'active' : 'inactive'}
                onChange={(e) => handleInputChange('isActive', e.target.value === 'active')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <p className="text-xs text-gray-500">Active categories are available for use</p>
            </div>
          </div>

          {/* Template Type Selection */}
          <div className="space-y-4">
            <div className="pb-2 border-b border-gray-200">
              {/* Template Type Radio Buttons */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="templateType"
                    value="1"
                    checked={formData.templateType === '1'}
                    onChange={(e) => handleInputChange('templateType', e.target.value)}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Default Fields</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="templateType"
                    value="2"
                    checked={formData.templateType === '2'}
                    onChange={(e) => handleInputChange('templateType', e.target.value)}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Custom Fields</span>
                </label>
              </div>
            </div>

            {/* Template Fields Section - Only show when Custom Fields (2) is selected */}
            {formData.templateType === '2' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Template Fields</h3>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addTemplateField}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Field
                  </Button>
                </div>

                <p className="text-sm text-gray-600">
                  Define custom fields that will appear when users create tickets with this category.
                </p>

                {loadingTemplate && (category?.categoryId || category?.id) ? (
                  <div className="text-center py-8 border border-gray-200 rounded-lg bg-gray-50">
                    <p className="text-sm text-gray-600">Loading template fields…</p>
                  </div>
                ) : templateFields.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-2">No template fields added</p>
                    <p className="text-xs text-gray-500">Click "Add Field" to create custom fields for this category</p>
                  </div>
                ) : (
                  templateFields.map((field, index) => (
                  <div
                    key={field.fieldId != null ? `field-${field.fieldId}` : `new-${index}`}
                    className={`border rounded-lg p-4 bg-white dark:bg-gray-800 ${
                      field.isActive === false
                        ? 'border-dashed border-amber-300 bg-amber-50/30 dark:border-amber-700 dark:bg-amber-950/20'
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <GripVertical className="h-5 w-5 text-gray-400 shrink-0" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Field {index + 1}</span>
                        {field.isActive === false && (
                          <span className="text-xs font-medium uppercase tracking-wide text-amber-800 bg-amber-100 px-2 py-0.5 rounded dark:bg-amber-900/50 dark:text-amber-200">
                            Inactive
                          </span>
                        )}
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer shrink-0 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={field.isActive !== false}
                          onChange={(e) => updateTemplateField(index, 'isActive', e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span>Active on ticket form</span>
                      </label>
                    </div>

                    <div className="space-y-2" data-validation-field={`fieldName_${index}`}>
                      <Label className="text-sm font-medium text-gray-700">
                        Field Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={field.fieldName}
                        onChange={(e) => updateTemplateField(index, 'fieldName', e.target.value)}
                        placeholder="e.g., Serial Number"
                        className={errors[`fieldName_${index}`] ? 'border-red-500' : ''}
                      />
                      {errors[`fieldName_${index}`] && (
                        <p className="text-xs text-red-500">{errors[`fieldName_${index}`]}</p>
                      )}
                      {field.fieldCode && (
                        <p className="text-xs text-gray-500">
                          Field Code: <span className="font-mono font-medium">{field.fieldCode}</span>
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2" data-validation-field={`fieldType_${index}`}>
                        <Label className="text-sm font-medium text-gray-700">
                          Field Type <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={field.fieldType}
                          onValueChange={(value) => updateTemplateField(index, 'fieldType', value)}
                        >
                          <SelectTrigger className={errors[`fieldType_${index}`] ? 'border-red-500' : ''}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {fieldTypes.map(type => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors[`fieldType_${index}`] && (
                          <p className="text-xs text-red-500">{errors[`fieldType_${index}`]}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700">
                          Required
                        </Label>
                        <div className="flex items-center gap-2 pt-2">
                          <input
                            type="checkbox"
                            checked={field.isRequired}
                            onChange={(e) => updateTemplateField(index, 'isRequired', e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-600">This field is required</span>
                        </div>
                      </div>
                    </div>

                    {(field.fieldType === 'select' || field.fieldType === 'checkbox') && (
                      <div className="mt-4">
                        <FieldOptionsChipsEditor
                          value={field.fieldOptions}
                          onChange={(json) => updateTemplateField(index, 'fieldOptions', json)}
                          error={errors[`fieldOptions_${index}`]}
                          validationFieldKey={`fieldOptions_${index}`}
                        />
                      </div>
                    )}
                  </div>
                  ))
                )}
              </div>
            )}
          </div>

      </form>

    </Modal>
  );
};

export default AddEditCategoryModal;
